import { TranslatedOption } from '@statsbomb/parachute-types';
import { t, TFunction } from 'i18next';
import { snakeToCamel } from './queries';

export const getTranslatedOption = <T>({ translation, value }: TranslatedOption<T>) => ({
  label: t(translation.key, {
    ns: translation.ns,
    count: translation.count,
  }),
  value,
});

export const getTranslatedOptions = <T>(options: TranslatedOption<T>[]) =>
  options.map(option => getTranslatedOption(option));

export const getTranslationMetrics = (id: string, translationNs: string = 'metrics') => {
  const translationKey =
    translationNs === 'events'
      ? `keys.${id}`
      : `${snakeToCamel(id.replace('aggregates.', '').replace('Per90', '').replace('_per_90', ''))}`;

  return translationKey;
};

export const getMetricTranslations = (t: TFunction, metricKey: string, translationNs: string = 'metrics') => {
  const translationKey = getTranslationMetrics(metricKey, translationNs);

  return {
    name: t(`${translationKey}.name`, { ns: translationNs, defaultValue: '' }),
    longName: t(`${translationKey}.longName`, { ns: translationNs, defaultValue: '' }),
    description: t(`${translationKey}.description`, { ns: translationNs, defaultValue: '' }),
  };
};

export const getTranslationColumns = (id: string) => {
  const isMetric = id.includes('aggregates');
  const translationNs = isMetric ? 'metrics' : 'events';
  const translationKey = `${getTranslationMetrics(id, translationNs)}${isMetric ? '.name' : ''}`;
  const translationKeyLong = isMetric ? `${getTranslationMetrics(id, translationNs)}.longName` : undefined;

  return { translationNs, translationKey, translationKeyLong };
};

export const stripTags = (text: string) => text.replace(/<[^>]*>/g, '');

export const flattenKeys = (messages: Record<string, unknown>, prefix = ''): Record<string, string> =>
  Object.entries(messages).reduce<Record<string, string>>((acc, [key, value]) => {
    const fullKey = prefix ? `${prefix}.${key}` : key;
    if (typeof value === 'object' && value !== null)
      Object.assign(acc, flattenKeys(value as Record<string, unknown>, fullKey));
    else if (typeof value === 'string') acc[fullKey] = value;
    return acc;
  }, {});

export const nestKeys = (flat: Record<string, string>): Record<string, unknown> => {
  const result: Record<string, unknown> = {};
  Object.entries(flat)
    .sort((a, b) => a[0].split('.').length - b[0].split('.').length)
    .forEach(([key, value]) => {
      const parts = key.split('.');
      const last = parts.pop()!;
      let node = result;
      let stopped = false;
      parts.forEach((part, i) => {
        if (stopped) return;
        if (typeof node[part] === 'string') {
          node[[part, ...parts.slice(i + 1), last].join('.')] = value;
          stopped = true;
          return;
        }
        if (typeof node[part] !== 'object' || node[part] === null) node[part] = {};
        node = node[part] as Record<string, unknown>;
      });
      if (!stopped) node[last] = value;
    });
  return result;
};
